import { createBrowserRouter, redirect } from 'react-router-dom';
import AuthPage from '../pages/AuthPage.jsx';
import NotFoundPage from '../pages/NotFoundPage.jsx';
import App from '@/App.jsx';
import { getTokens, verifyTokenAndGetUser } from '@/network/auth.service.js';
import { useUserStore } from '@/stores/useUser.store.js';
import { createUser, getUserDetailByUserId } from '@/network/api.service.js';
import AdminPage from '@/pages/AdminPage.jsx';
import HomePage from '@/pages/HomePage.jsx';
import EstatesPage from '@/pages/EstatesPage.jsx';
import CreateEstatePage from '@/pages/CreateEstatePage.jsx';
import LotCreationPage from '@/pages/LotCreationPage.jsx';
import PreProcessPage from '@/pages/PreProcessPage.jsx';
import PreProcessEditPage from '@/pages/PreProcessEditPage.jsx';
import EstateDetailPage from '@/pages/EstateDetailPage.jsx';
import ProcessesPage from '@/pages/process-screens/ProcessesPage.jsx';
import ProcessStepViewerPage from '@/pages/process-screens/ProcessStepViewerPage.jsx';
import ProcessStepCompletePage from '@/pages/process-screens/ProcessStepCompletePage.jsx';
import DefineProcessStepPage from '@/pages/process-screens/DefineProcessStepPage.jsx';
import DefinedProcessStepPage from '@/pages/process-screens/DefinedProcessStepPage.jsx';
import DryingLotsPage from '@/pages/drying-stage-screens/DryingLotsPage.jsx';
import DryingStartPage from '@/pages/drying-stage-screens/DryingStartPage.jsx';
import DryingInProgressPage from '@/pages/drying-stage-screens/DryingInProgressPage.jsx';
import DryingLotBedActivePage from '@/pages/drying-stage-screens/DryingLotBedActivePage.jsx';
import QRScannerPage from '@/pages/QRScannerPage.jsx';
import LotsPage from '@/pages/lot/LotsPage.jsx';
import LotDetailPage from '@/pages/lot/LotDetailPage.jsx';
import PreProcessDetailPage from '@/pages/PreProcessDetailPage.jsx';
import UpdatePreProcessModalPage from '@/components/modal/UpdatePreProcessModal.jsx';
import UpdateProcessStageInformationPage from '@/pages/process-screens/UpdateProcessStageInformationPage.jsx';
import StorageLotsPage from '@/pages/storage-screens/StorageLotsPage.jsx';
import StorageLotDetailPage from '@/pages/storage-screens/StorageLotDetailPage.jsx';

const { setAuth, setUser } = useUserStore.getState();

const checkRole = allowedRoles => {
  const user = useUserStore.getState().user; // Redirect if not authenticated

  if (!user || !user.role) {
    return false;
  }

  const userRole = user.role.toLowerCase();
  const normalizedRoles = allowedRoles.map(r => r.toLowerCase());

  return normalizedRoles.includes(userRole);
};

const authLoader = async () => {
  const params = new URLSearchParams(window.location.search);
  const authcode = params.get('authcode');

  try {
    // 1. If authcode is present → exchange code for tokens
    if (authcode) {
      const { data } = await getTokens({ authcode });

      localStorage.setItem('Authorization', data.data.accessToken);
      localStorage.setItem('RefreshToken', data.data.refreshToken);

      setAuth(true);
      // optional: remove authcode from URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // 2. No authcode → check local token
    const token = localStorage.getItem('Authorization');
    if (!token) {
      setAuth(false);
      return redirect('/auth');
    }

    // 3. Verify token with backend
    try {
      // const { data } = await verifyTokenAndGetUser(); // <-- hit /verify-tokens here
      const response = await verifyTokenAndGetUser();

      // Validate response structure before accessing nested properties
      if (!response || !response.data || !response.data.data) {
        console.error('Invalid response from verifyTokenAndGetUser:', response);
        localStorage.removeItem('Authorization');
        localStorage.removeItem('RefreshToken');
        setAuth(false);
        return redirect('/auth');
      }

      const { data } = response;

      // Additional validation for required fields
      if (!data.data.user_id) {
        console.error('Missing required user data:', data.data);
        localStorage.removeItem('Authorization');
        localStorage.removeItem('RefreshToken');
        setAuth(false);
        return redirect('/auth');
      }

      if (authcode) {
        await createUser({
          fullname: data.data.fullname,
          phone_number: data.data.phone_number,
          role: data.data.role,
          user_id: data.data.user_id,
        });
      }
      const { data: userData } = await getUserDetailByUserId(data.data.user_id);

      setUser({
        ...data.data,
        // user id is for SoCoffee backend
        user_id: userData?.data?._id,
      });

      localStorage.setItem('user_id', userData?.data?._id);
      setAuth(true);
    } catch (error) {
      // If backend says token invalid/expired → logout
      if (error.response && [401, 403].includes(error.response.status)) {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('RefreshToken');
        localStorage.removeItem('user_id');
        setAuth(false);
        return redirect('/auth');
      }

      // any other error: you can decide to let it pass or redirect
      console.error('verifyTokens error:', error);
      return redirect('/auth');
    }

    // 4. All good
    setAuth(true);
    return null;
  } catch (err) {
    console.error('authLoader error:', err);
    setAuth(false);
    return redirect('/auth');
  }
};

// Public loader function (redirect if already authenticated)
const publicLoader = () => {
  const token = localStorage.getItem('Authorization');
  if (token) {
    return redirect('/');
  }
  return null;
};

// Admin loader - requires admin role
const adminLoader = async () => {
  // First run auth check
  const authResult = await authLoader();
  if (authResult) return authResult;

  // Check if user has admin role
  if (!checkRole(['admin'])) {
    console.log('in block');

    // Redirect to home if not admin
    return redirect('/');
  }

  return null;
};

// // Role-specific loader factory
// const roleLoader = allowedRoles => async () => {
//   // First run auth check
//   const authResult = await authLoader();
//   if (authResult) return authResult;

//   // Check if user has required role
//   if (!checkRole(allowedRoles)) {
//     return redirect('/'); // or redirect to unauthorized page
//   }

//   return null;
// };

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    // loader: authLoader,
    errorElement: <NotFoundPage />,
    children: [
      {
        path: '/',
        index: true,
        loader: authLoader,
        element: <HomePage />,
      },
      {
        path: 'admin',
        element: <AdminPage />,
      },
      {
        path: 'estates',
        loader: adminLoader,
        element: <EstatesPage />,
      },
      {
        path: 'estates/add',
        // loader: adminLoader,
        element: <CreateEstatePage />,
      },
      {
        path: 'estates/detail/:id',
        // loader: adminLoader,
        element: <EstateDetailPage />,
      },
      {
        path: 'pre-process',
        element: <PreProcessPage />,
      },
      {
        path: 'pre-process/:id',
        element: <PreProcessEditPage />,
      },
      {
        path: 'source',
        // loader: adminLoader,
        element: <LotCreationPage />,
      },
      {
        path: 'pre-process/detail/:id',
        element: <PreProcessDetailPage />,
      },
      {
        path: 'pre-process/detail/:id/update',
        element: <UpdatePreProcessModalPage />,
      },
      {
        path: 'lots',
        children: [
          {
            index: true,
            element: <LotsPage />,
          },
          {
            path: 'lot-detail/:id',
            loader: authLoader,
            element: <LotDetailPage />,
          },
        ],
      },
      // Process Related Routes
      {
        path: 'process',
        children: [
          {
            index: true,
            element: <ProcessesPage />,
          },
          {
            path: 'lot/:id',
            element: <DefineProcessStepPage />,
          },
          {
            path: 'lot/:id/step',
            element: <DefinedProcessStepPage />,
          },
          // lot/${id}/step/${process_data?._id}
          {
            path: 'lot/:id/step/:step_id',
            element: <ProcessStepViewerPage />,
          },
          {
            path: 'lot/:id/step/:step_id/complete',
            element: <ProcessStepCompletePage />,
          },
          {
            path: ':id/step/:step_id/update',
            element: <UpdateProcessStageInformationPage />,
          },
        ],
      },
      {
        path: 'drying-lots',
        children: [
          {
            index: true,
            element: <DryingLotsPage />,
          },
        ],
      },
      {
        path: 'drying-lots/start/:lot_id',
        element: <DryingStartPage />,
      },
      {
        path: 'drying-lots/in-progress/:lot_id',
        element: <DryingInProgressPage />,
      },
      {
        path: 'drying-lots/in-progress/:lot_id/bed/:drying_lot_id',
        loader: authLoader,
        element: <DryingLotBedActivePage />,
      },
      {
        path: 'storage-lots',
        children: [
          {
            index: true,
            element: <StorageLotsPage />,
          },
          {
            path: ':lot_id',
            element: <StorageLotDetailPage />,
          },
        ],
      },
    ],
  },

  {
    path: '/qr-scanner',
    element: <QRScannerPage />,
  },
  {
    path: '/auth',
    loader: publicLoader,
    element: <AuthPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

export default router;
